'use client';

import Image, { ImageProps } from 'next/image';
import React, { useState } from 'react';
import { cn } from '@/lib/utils';

export interface AppImageProps extends Omit<ImageProps, 'width' | 'height'> {
  fallbackText?: string;
  shape?: 'circle' | 'square';
  size?: 'sm' | 'md' | 'lg' | 'full';
  gradient: boolean;
  className?: string;
}

export function AppImage({
  src,
  alt,
  fallbackText,
  shape = 'square',
  size = 'full',
  gradient = false,
  className,
  onLoad,
  ...rest
}: AppImageProps) {
  const [loading, setLoading] = useState(true);

  const handleLoad = (event: React.SyntheticEvent<HTMLImageElement>) => {
    setLoading(false);
    if (onLoad) onLoad(event);
  };

  const getInitials = (name: string) => {
    const parts = name.trim().split(' ');
    return parts.length >= 2
      ? `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase()
      : name.charAt(0).toUpperCase();
  };

  const fallbackTextSizeClasses = {
    sm: 'text-base',
    md: 'text-xl',
    lg: 'text-3xl',
    full: 'text-4xl',
  };

  const dimensionClasses = {
    sm: 'h-10 w-10',
    md: 'h-16 w-16',
    lg: 'h-24 w-24',
    full: 'h-full w-full',
  };

  const rootDivClassName = cn(
    'relative flex-shrink-0',
    dimensionClasses[size],
    className
  );

  if (!src) {
    return (
      <div
        className={cn(
          rootDivClassName,
          'flex items-center justify-center',
          shape === 'circle' ? 'rounded-full' : 'rounded-lg',
          gradient
            ? 'bg-gradient-to-r from-accent-blue/80 to-accent-purple/80'
            : 'bg-primary-200'
        )}
      >
        <div className="absolute inset-0 bg-white/10 mix-blend-overlay" />
        <span
          className={cn(
            'font-bold tracking-widest text-white opacity-90',
            fallbackTextSizeClasses[size]
          )}
        >
          {getInitials(fallbackText || 'P')}
        </span>
      </div>
    );
  }

  return (
    <div className={rootDivClassName}>
      <Image
        src={src}
        alt={alt}
        fill
        sizes={
          size === 'sm'
            ? '40px'
            : size === 'md'
              ? '64px'
              : size === 'lg'
                ? '96px'
                : '(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw'
        }
        className={cn(
          'absolute inset-0 h-full w-full object-cover',
          shape === 'circle' ? 'rounded-full' : 'rounded-lg',
          loading && 'animate-pulse'
        )}
        onLoad={handleLoad}
        {...rest}
      />
    </div>
  );
}
